/* eslint-disable webflow/module-top-level-imports */
import {
  type ExpressionF,
  type ElementF,
  fromBrandedExpressionF,
  _EVariable,
  _ERecord,
  _ECall,
  _EFunction,
  _EUnion,
  _ECase,
  _EList,
  _EElement,
  _ETyped,
  _ENumber,
  _EText,
  _EBoolean,
  _ESelect,
  _EData,
  _ELiteral,
  _EHole,
  _EEnum,
  _EExtend,
  _EDistinct,
} from '@packages/systems/wfdl/GenericExpression/GenericExpressionTypes';
/* eslint-enable webflow/module-top-level-imports */

export interface Expression extends ExpressionF<Expression> {}

export type ExpressionElement = ElementF<Expression>;

export const EVariable = _EVariable<Expression>;
export const ERecord = _ERecord<Expression>;
export const ECall = _ECall<Expression>;
export const EFunction = _EFunction<Expression>;
export const EUnion = _EUnion<Expression>;
export const ECase = _ECase<Expression>;
export const EList = _EList<Expression>;
export const EElement = _EElement<Expression>;
/** @deprecated: The `Typed` expression is being removed. Types can be declared in a component `dataType` or a plugin value `envType`. */
export const ETyped = _ETyped<Expression>;
export const EText = _EText<Expression>;
/** @deprecated: Use True and False from @packages/systems/plugins-utils/Boolean. */
export const EBoolean = _EBoolean<Expression>;
export const ENumber = _ENumber<Expression>;
export const ESelect = _ESelect<Expression>;
export const EData = _EData<Expression>;
export const ELiteral = _ELiteral<Expression>;
export const EHole = _EHole<Expression>;
/** @deprecated: Use `EUnion` with empty records as values. */
export const EEnum = _EEnum<Expression>;
export const EExtend = _EExtend<Expression>;
export const EDistinct = _EDistinct<Expression>;

/**
 * Used by tooling to dynamically detect if an object is an Expression.
 */
function isExpressionType(obj: any): boolean {
  // The type magic here is to win TS's help in keeping this function
  // up to date using exhaustive checking by casting to empty.
  const branded: Expression = obj as Expression;
  const expression = fromBrandedExpressionF(branded);

  switch (expression.type) {
    case 'Boolean':
    case 'Call':
    case 'Case':
    case 'Element':
    case 'Enum':
    case 'Extend':
    case 'Hole':
    case 'List':
    case 'Literal':
    case 'Function':
    case 'Number':
    case 'Record':
    case 'Select':
    case 'Text':
    case 'Union':
    case 'Variable':
    case 'Typed':
    case 'Distinct':
      return true;

    default: {
      const _: never = expression;
      return false;
    }
  }
}

function isExpression(obj: any): boolean {
  if (
    typeof obj === 'object' &&
    !Array.isArray(obj) &&
    obj !== null &&
    // objects with null protoype can pass the above conditions
    // and cause a crash when obj.hasOwnProperty is called
    typeof obj.hasOwnProperty === 'function'
  ) {
    return (
      obj.hasOwnProperty('type') &&
      obj.hasOwnProperty('val') &&
      Object.keys(obj).length === 2 && // has ONLY `type` and `val` props
      isExpressionType(obj)
    );
  }
  return false;
}

export function getExpressionFromAny(obj: any): Expression | null {
  if (!isExpression(obj)) return null;
  return obj as Expression;
}
